import React, { useState, useEffect } from 'react';
import { COLORS } from '../../utils/constants';
import { adminAPI } from '../../services/api';
import { Settings, Save } from 'lucide-react';
import { useAdminToast } from '../../hooks/useAdminToast';

// Import section components
import {
  CryptoAddressesSection,
  QRCodesSection,
  ClientStatusesSection,
  TagsSection,
  PlatformSettingsSection,
  QRPreviewModal
} from './components/settings';

const AdminSettings = () => {
  const { toast } = useAdminToast();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showQRPreview, setShowQRPreview] = useState(null);
  
  // Tags state
  const [predefinedTags, setPredefinedTags] = useState([]);

  useEffect(() => {
    loadSettings();
    loadTags();
  }, []);

  const loadSettings = async () => {
    try {
      const res = await adminAPI.getSystemSettings();
      setSettings(res.data);
    } catch (err) {
      console.error('Failed to load settings:', err);
    } finally {
      setLoading(false);
    }
  };
  
  const loadTags = async () => {
    try {
      const res = await adminAPI.getAllTags();
      setPredefinedTags(res.data.predefined || []);
    } catch (err) {
      console.error('Failed to load tags');
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await adminAPI.updateSystemSettings(settings);
      toast.saved('Настройки');
    } catch (err) {
      toast.apiError(err, 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  const downloadQR = (address, currency) => {
    const svg = document.getElementById(`qr-preview-${currency}`);
    if (!svg) return;
    
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const data = new XMLSerializer().serializeToString(svg);
    const img = new Image();
    
    img.onload = () => {
      canvas.width = 400;
      canvas.height = 400;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 50, 50, 300, 300);
      
      const link = document.createElement('a');
      link.download = `${currency.toLowerCase().replace(/[^a-z0-9]/g, '_')}_qr.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    };
    
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(data)));
  };

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: COLORS.textSecondary }}>
        Загрузка настроек...
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', maxWidth: '900px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '24px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Settings size={28} color={COLORS.primary} />
          <h1 style={{ color: COLORS.text, fontSize: '24px', fontWeight: '700', margin: 0 }}>
            Настройки
          </h1>
        </div>
        <button
          onClick={saveSettings}
          disabled={saving}
          style={{
            padding: '12px 24px',
            backgroundColor: saving ? COLORS.border : COLORS.primary,
            border: 'none',
            borderRadius: '10px',
            color: '#000',
            fontSize: '15px',
            fontWeight: '600',
            cursor: saving ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <Save size={18} />
          {saving ? 'Сохранение...' : 'Сохранить'}
        </button>
      </div>

      {/* Crypto Addresses */}
      <CryptoAddressesSection settings={settings} setSettings={setSettings} />

      {/* QR Codes */}
      <QRCodesSection 
        settings={settings} 
        onPreview={setShowQRPreview}
        onDownload={downloadQR}
      />

      {/* Client Statuses */}
      <ClientStatusesSection settings={settings} setSettings={setSettings} />

      {/* Tags */}
      <TagsSection 
        predefinedTags={predefinedTags} 
        setPredefinedTags={setPredefinedTags}
        onSaveTags={async () => {
          try {
            await adminAPI.saveTags(predefinedTags);
            toast.saved('Теги');
          } catch (err) {
            toast.apiError(err, 'Ошибка сохранения тегов');
          }
        }}
      />

      {/* Platform Settings */}
      <PlatformSettingsSection settings={settings} setSettings={setSettings} />

      {/* QR Preview Modal */}
      {showQRPreview && (
        <QRPreviewModal 
          currency={showQRPreview.currency} 
          address={showQRPreview.address} 
          onClose={() => setShowQRPreview(null)}
          onDownload={downloadQR}
        />
      )}
    </div>
  );
};

export default AdminSettings;
